import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Theme } from "../../../../theme";
import { useTranslation } from "react-i18next";

interface InputListProps {
  value: string[];
  onChange: (newList: string[]) => void;
  readOnly?: boolean;
}

function inputsFromProps(value: string[], readOnly: boolean | undefined) {
  if (!readOnly && value.length === 0) {
    return [""];
  }
  return value.filter((i: string) => i !== "");
}

function shallowEqualStringArrays(a: string[], b: string[]) {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

export default function InputList({
  value,
  onChange,
  readOnly,
}: InputListProps) {
  const [inputs, setInputs] = useState(() => inputsFromProps(value, readOnly));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const next = inputsFromProps(value, readOnly);
    setInputs((prev) => (shallowEqualStringArrays(prev, next) ? prev : next));
  }, [readOnly, value]);

  const handleAddInput = () => {
    if (readOnly) return;
    setInputs((prev) => {
      const newInputs = [...prev, ""];
      return newInputs;
    });

    setTimeout(() => {
      const lastInput = inputRefs.current[inputRefs.current.length - 1];
      lastInput?.focus();
    }, 0);
  };

  const handleMoveItem = (index: number, delta: -1 | 1) => {
    if (readOnly) return;
    const to = index + delta;
    if (to < 0 || to >= inputs.length) {
      return;
    }
    const newInputs = [...inputs];
    [newInputs[index], newInputs[to]] = [newInputs[to], newInputs[index]];
    setInputs(newInputs);
    onChange(newInputs);
  };

  const handleRemoveInput = (index: number) => {
    if (readOnly) return;
    const newInputs = inputs.filter((_, i) => i !== index);
    setInputs(newInputs);
    onChange(newInputs);
  };

  const handleChangeInput = (index: number, value: string) => {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
    onChange(newInputs);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleAddInput();
    }
  };

  return (
    <div className="relative space-y-2">
      {!readOnly && (
        <button
          type="button"
          onClick={handleAddInput}
          className="p-2 rounded-2xl hover:bg-gray-200 dark:hover:bg-gray-700 absolute top-[-35px] right-0"
        >
          <Plus className="w-4 h-4 text-gray-900 dark:text-gray-100" />
        </button>
      )}

      {inputs.length === 0 && (
        <div className="text-gray-500 dark:text-gray-400 text-sm">
          <small>{t("form.emptyList")}</small>
        </div>
      )}

      {inputs.map((input, index) => (
        <div key={index} className="group relative w-full min-w-0">
          <input
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            value={input}
            onChange={(e) => handleChangeInput(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e)}
            placeholder=""
            readOnly={readOnly}
            className={`${Theme.input} w-full min-w-0 box-border ${
              !readOnly ? (inputs.length > 1 ? "!pr-24" : "!pr-12") : ""
            }`}
          />
          {!readOnly && (
            <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center">
              <div className="pointer-events-auto flex items-center gap-0.5">
                {inputs.length > 1 && (
                  <div className="flex items-center gap-0.5 opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto">
                    <button
                      type="button"
                      onClick={() => handleMoveItem(index, -1)}
                      disabled={index === 0}
                      aria-label={t("form.moveItemUp")}
                      title={t("form.moveItemUp")}
                      className="rounded-md p-1 text-gray-600 hover:bg-gray-200/90 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700/90"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveItem(index, 1)}
                      disabled={index === inputs.length - 1}
                      aria-label={t("form.moveItemDown")}
                      title={t("form.moveItemDown")}
                      className="rounded-md p-1 text-gray-600 hover:bg-gray-200/90 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700/90"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveInput(index)}
                  className="rounded-md p-1 text-red-500 hover:bg-red-100 dark:hover:bg-gray-700"
                  aria-label={t("form.delete")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
